import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Search, Wand2 } from 'lucide-react';

import { useHaptics } from '@/hooks/useHaptics';
import { useCardsQuery, useCompleteDeckMutation, useSaveDeckMutation } from '@/services/qbotApi';
import { CardIcon } from '@/components/cr/CardIcon';
import { cn } from '@/lib/cn';
import { saveDeckErrorKey } from './saveDeckError';
import { DoctorResult } from './AdvisorResults';

/** True when the RTK error is qbot's 404 NOT_LINKED (no linked CR tag). */
function isNotLinked(error: unknown): boolean {
  const e = error as { status?: number; data?: { error?: { code?: string } } } | undefined;
  return e?.status === 404 && e?.data?.error?.code === 'NOT_LINKED';
}
function isRateLimited(error: unknown): boolean {
  return (error as { status?: number } | undefined)?.status === 429;
}

const MAX_SEEDS = 7;

/**
 * "Build a deck" — pick a few cards you want, the deterministic qCrC completer
 * (NO AI) fills the missing slots from your collection and returns the ideal
 * 8-card deck with its rating, synergies and weaknesses.
 *
 * `complete-deck` builds from the caller's OWNED cards (linked tag), so
 * NOT_LINKED surfaces the link CTA. It's a picker → result flow, its own
 * scroller — not the fixed single-deck stage.
 */
export function DeckBuilderMode() {
  const { t } = useTranslation('cr');
  const haptics = useHaptics();
  const navigate = useNavigate();

  const { data: catalog, isLoading: catalogLoading, isError: catalogError } = useCardsQuery();
  const [build, buildState] = useCompleteDeckMutation();
  const [saveDeck, { isLoading: saving }] = useSaveDeckMutation();
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [seeds, setSeeds] = useState<number[]>([]);
  const [search, setSearch] = useState('');

  const cards = useMemo(() => catalog ?? [], [catalog]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q ? cards.filter((c) => c.name.toLowerCase().includes(q)) : cards;
    return [...list].sort((a, b) => a.elixir - b.elixir || a.name.localeCompare(b.name));
  }, [cards, search]);

  const toggle = (id: number) => {
    haptics.selection();
    setSeeds((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : prev.length >= MAX_SEEDS
          ? prev
          : [...prev, id],
    );
  };

  const onBuild = () => {
    if (seeds.length === 0) return;
    haptics.impact('medium');
    setSaved(false);
    setSaveError(null);
    buildState.reset();
    build({ seedCards: seeds }).catch(() => undefined);
  };

  const backToPicker = () => {
    buildState.reset();
    setSaved(false);
    setSaveError(null);
  };

  const result = buildState.data?.deck;

  const onSave = async () => {
    if (!result) return;
    setSaveError(null);
    try {
      await saveDeck({
        name: t('panel.deckBuilder.saveName', { archetype: result.archetype }),
        cards: result.cards.map((c) => c.name),
      }).unwrap();
      haptics.notification('success');
      setSaved(true);
    } catch (err) {
      haptics.notification('error');
      setSaveError(t(saveDeckErrorKey(err)));
    }
  };

  // ── Building / error / result states ──
  if (buildState.isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <Wand2 className="h-6 w-6 animate-pulse text-brand" />
        <p className="max-w-[16rem] text-xs text-fg-muted">{t('panel.deckBuilder.building')}</p>
      </div>
    );
  }

  if (buildState.error) {
    if (isNotLinked(buildState.error)) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 px-4 text-center">
          <p className="max-w-[16rem] text-sm text-fg-muted">{t('panel.link.prompt')}</p>
          <button
            type="button"
            onClick={() => {
              haptics.impact('light');
              navigate('/cr/link');
            }}
            className="rounded-lg bg-brand px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand-fg"
          >
            {t('panel.link.cta')}
          </button>
        </div>
      );
    }
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <p className="text-sm text-rose-300">
          {isRateLimited(buildState.error)
            ? t('panel.advisor.rateLimited')
            : t('panel.advisor.error')}
        </p>
        <button
          type="button"
          onClick={onBuild}
          className="text-[11px] font-bold uppercase tracking-wide text-brand"
        >
          {t('panel.retry')}
        </button>
      </div>
    );
  }

  if (result) {
    return (
      <div className="flex h-full flex-col gap-2">
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">
          <DoctorResult
            deck={result}
            onSave={onSave}
            saving={saving}
            saved={saved}
            saveError={saveError}
            saveLabel={t('panel.action.save')}
            savedLabel={t('panel.action.saved')}
          />
        </div>
        <button
          type="button"
          onClick={backToPicker}
          className="shrink-0 rounded-lg bg-bg-secondary py-1.5 text-[11px] font-bold uppercase tracking-wide text-brand"
        >
          {t('panel.deckBuilder.changeCards')}
        </button>
      </div>
    );
  }

  // ── Picker ──
  return (
    <div className="flex h-full flex-col gap-2">
      <div className="shrink-0">
        <h2 className="text-sm font-semibold text-fg">{t('panel.deckBuilder.title')}</h2>
        <p className="text-[11px] leading-snug text-fg-muted">{t('panel.deckBuilder.subtitle')}</p>
      </div>

      {/* h-11: la ricerca era alta 30px scarsi, sotto la soglia comoda per un
          dito e visivamente un dettaglio invece di un comando. Stessa altezza
          dei campi in Amici, così i campi di testo dell'app si somigliano. */}
      <div className="relative shrink-0">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('panel.deckBuilder.searchPlaceholder')}
          className="card-edge h-11 w-full rounded-xl bg-bg-secondary pl-9 pr-3 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-1 focus:ring-brand"
        />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">
        {catalogLoading ? (
          <p className="py-6 text-center text-xs text-fg-muted">
            {t('panel.deckBuilder.loadingCards')}
          </p>
        ) : catalogError ? (
          <p className="py-6 text-center text-xs text-rose-300">
            {t('panel.deckBuilder.cardsError')}
          </p>
        ) : filtered.length === 0 ? (
          <p className="py-6 text-center text-xs text-fg-muted">{t('panel.deckBuilder.noCards')}</p>
        ) : (
          <div className="grid grid-cols-5 gap-1.5">
            {filtered.map((c) => {
              const on = seeds.includes(c.id);
              return (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggle(c.id)}
                  className={cn(
                    'rounded-md transition-opacity active:opacity-80',
                    on ? 'ring-2 ring-brand' : 'opacity-80',
                  )}
                >
                  <CardIcon id={c.id} name={c.name} iconUrl={c.iconUrl} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <span className="shrink-0 text-[11px] tabular-nums text-fg-muted">
          {t('panel.deckBuilder.count', { n: seeds.length, max: MAX_SEEDS })}
        </span>
        {seeds.length > 0 && (
          <button
            type="button"
            onClick={() => setSeeds([])}
            className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-fg-muted"
          >
            {t('panel.deckBuilder.clear')}
          </button>
        )}
        {/* h-11: è l'azione conclusiva della schermata e stava alta quanto
            un'etichetta. Ora ha la stessa statura del campo di ricerca sopra,
            e con `min-w` non si restringe fino a diventare un francobollo
            quando la riga si affolla di contatore e «pulisci». */}
        <button
          type="button"
          disabled={seeds.length === 0}
          onClick={onBuild}
          className="ml-auto flex h-11 min-w-[7.5rem] items-center justify-center gap-1.5 rounded-xl bg-brand px-4 text-sm font-bold uppercase tracking-wide text-brand-fg shadow-lg shadow-brand/20 transition-opacity active:opacity-80 disabled:opacity-30 disabled:shadow-none"
        >
          <Wand2 className="h-4 w-4" />
          {t('panel.deckBuilder.build')}
        </button>
      </div>
    </div>
  );
}
